import { ResponseCatalog, RequestParams, CertificateModel } from 'Models'
import { all, call, delay, put, select, takeLeading } from 'typed-redux-saga'
import {
	errorHandler,
	NOTIFICATION_TYPE,
	showNotificationAction,
	notifyMessageCreator,
	deviceDetect,
	gridSlice,
	fileApi,
	loadersSlice,
} from 'Modules'
import { Deserialize } from 'cerialize'
import { getUserCertificates, createSignature } from 'crypto-pro'
import { getSignActProtocolTemplate, getDate, downloadFile, getBase64File, readFile } from 'Helpers'
import { urls } from 'Src/constants/Urls'
import { Callback } from 'Src/types/utilTypes'
import { get } from 'lodash'
import { getCodeTranslate } from 'LibNeo'
import {
	KindActModel,
	actSlice,
	ActsSelector,
	actUnpaidSlice,
	actNS,
	ActModel,
	GenerateProtocolStatuses,
	ActStatuses,
	ProtocolPartnerType,
	ProtocolDsType,
	ActsApi,
	ERROR_CODE_SMS,
} from 'Pages/acts'

function* getAllActs(clear?: boolean, requestParams?: RequestParams, callback?: Callback) {
	const openedAct = yield* select(ActsSelector.getOpenedAct)
	let params = yield* select(ActsSelector.getActsRequestParams)
	let oldActs = new ResponseCatalog<ActModel>()
	if (!clear && requestParams?.page !== 1) {
		oldActs = yield* select(ActsSelector.getActs)
	} else if (clear) {
		const { page, per_page = 30, query = undefined, ...rest } = { ...params, page: 1 }
		params = new RequestParams(page, per_page, query, rest)
		yield* put(actSlice.actions.setChosenActs([]))
		yield* put(
			actUnpaidSlice.actions.setUnpaidActsCounter({
				on_partner_signing_single: 0,
				on_partner_signing_several: 0,
				amount_more_5k: 0,
			})
		)
	}
	yield* put(actSlice.actions.setActsRequestParams(requestParams || params))
	const res = yield* call(ActsApi.getAllActs, requestParams || params)
	const acts = new ResponseCatalog<ActModel>(res)
	const newActs = res.entries.map((act): ActModel => Deserialize(act, ActModel))
	acts.entries = [...oldActs.entries, ...newActs]
	yield* put(actSlice.actions.setActs(acts))
	if (openedAct) {
		const r = yield* call(ActsApi.getAct, openedAct)
		yield* put(actSlice.actions.setAct(Deserialize(r, ActModel)))
	}
	callback?.()
}

function* getSignableActsSaga() {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const params = new RequestParams(1, 1, null, { signable: true })
		const res = yield* call(ActsApi.getAllActs, params)
		const signableActs = new ResponseCatalog<ActModel>(res)
		yield* put(actSlice.actions.setSignableActsCount(signableActs.total_entries))
		yield* put(gridSlice.actions.setLoading(false))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getActsSaga(action: actNS.getActs) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear, callback } = action.payload
		yield* call(getAllActs, clear, params, callback)
		yield* put(gridSlice.actions.setLoading(false))
	} catch (error) {
		yield* put(gridSlice.actions.setLoading(false))
		yield* call(errorHandler, error)
	}
}

function* getActSaga(action: actNS.getAct) {
	try {
		const isMobile = yield* select(deviceDetect.getIsMobile)
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(ActsApi.getAct, action.payload)
		if (isMobile) {
			yield* put(actSlice.actions.setItem(Deserialize(res, ActModel)))
		} else {
			yield* put(actSlice.actions.setAct(Deserialize(res, ActModel)))
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* uploadActsSaga(action: actNS.uploadActs) {
	try {
		yield* call(notifyMessageCreator, 'acts.upload.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadActs, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.upload.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* uploadActsStatusesSaga(action: actNS.uploadActsStatuses) {
	try {
		yield* call(notifyMessageCreator, 'common.statuses_upload.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadActsStatuses, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.statuses_upload.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* cancelActsPaidSaga() {
	try {
		yield* call(ActsApi.cancelActsPaids)
		yield* call(notifyMessageCreator, 'acts.cancel_paid.success')
		yield* call(getAllActs, true)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* createActsSMSCodeSaga(action: actNS.createActSmsCode) {
	try {
		yield* put(actSlice.actions.setTimer(null))
		const { callback, ids } = action.payload
		const res = yield* call(ActsApi.createActSmsCode, ids)
		yield* put(actSlice.actions.setTimer(res?.[ERROR_CODE_SMS]))
		callback?.()
	} catch (error) {
		const { errorCallback } = action.payload
		const timer = error?.data.errors.filter((item) =>
			Object.keys(item).includes(ERROR_CODE_SMS)
		)?.[0]?.[ERROR_CODE_SMS]
		if (error?.data.errors[0]?.too_many_failed_input_sms) {
			yield* call(notifyMessageCreator, 'acts.exceeded_number_of_attempts_to_sign', {
				options: { type: NOTIFICATION_TYPE.ERROR },
			})
		} else if (!timer) {
			yield* call(errorHandler, error)
		}
		yield* put(actSlice.actions.setTimer(timer))
		errorCallback?.(timer)
	}
}

function* makeProtocolSaga(act: ActModel) {
	const template = getSignActProtocolTemplate(act)
	const ProtocolBuilder = yield* call(() =>
		import('Helpers/Pdf/ProtocolBuilder').then((data) => data.default)
	)
	const protocolBuilder = new ProtocolBuilder(template)
	return protocolBuilder.output()
}

function* sendProtocolSaga(act: ActModel) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const protocolGenerationValidation = yield* select(ActsSelector.getProtocolGenerationValidation)
		const protocolBlob = yield* call(makeProtocolSaga, act)
		const onPartner =
			act?.status?.status_slug === ActStatuses.ON_PARTNER_SIGNING ||
			act?.status?.status_slug === ActStatuses.ON_DS_SIGNING
		const isNoValide = validateAct(act, onPartner, act.certificate_info)
		if (isNoValide) {
			if (protocolGenerationValidation < 1) {
				yield* put(actSlice.actions.setUserSertificates([]))
				yield* put(
					actSlice.actions.counterProtocolGenerationValidation(protocolGenerationValidation + 1)
				)
				yield* call(generateProtocol, act?.id)
			} else {
				yield* put(actSlice.actions.counterProtocolGenerationValidation(0))
				yield* call(ActsApi.sendActProtocol, protocolBlob, act.id, isNoValide)
			}
		} else {
			yield* put(actSlice.actions.counterProtocolGenerationValidation(0))
			yield* call(ActsApi.sendActProtocol, protocolBlob, act.id)
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* partnerSignActsSaga(action: actNS.partnerSignActs) {
	try {
		yield* call(notifyMessageCreator, 'acts.sign.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { code, ids, callback } = action.payload
		yield* all(ids.map((act_id) => call(onValidate, { act_id, code, isPartner: true })))
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.sign.success')
		callback?.()
	} catch (error) {
		const { errorCallback } = action.payload
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const errors = error?.data?.errors
		errorCallback?.(errors)
		yield* call(errorHandler, error)
	}
}
function* partnerSignAct(act: ActModel, act_id: number) {
	try {
		const protocol = yield* call(
			makeProtocolSaga,
			Deserialize(
				{
					...act,
					partner_signing_at: new Date().toISOString().split('T').join(' '),
				},
				ActModel
			)
		)
		const protocolGenerationValidation = yield* select(ActsSelector.getProtocolGenerationValidation)
		const isNoValide = validateAct(act, true)
		if (protocolGenerationValidation > 1) {
			yield* call(ActsApi.sendActProtocol, protocol, act_id, isNoValide)
			yield* put(actSlice.actions.counterProtocolGenerationValidation(0))
		} else {
			yield* call(ActsApi.sendActProtocol, protocol, act_id)
			yield* put(actSlice.actions.counterProtocolGenerationValidation(0))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function validateAct(act: ActModel, isPartner: boolean, cert?: CertificateModel) {
	const actFileds = Object.values(ProtocolPartnerType).reduce((acc, field) => {
		if (field.includes('?.')) {
			const name = field.split('?.')
			return act[name?.[0]]?.[name?.[1]] ? acc : `${acc}act.${field}:${act[name[0]]?.[name[1]]},`
		}
		return act[field] ? acc : `${acc}act.${field}:${act[field]},`
	}, '')
	let certFileds
	if (!isPartner) {
		certFileds = Object.values(ProtocolDsType).reduce(
			(acc, field) => (cert[field] ? acc : `${acc}cert.${field}:${cert[field]}`),
			''
		)
	}
	return isPartner ? actFileds : actFileds + certFileds
}
function* onValidate({
	act_id,
	isPartner,
	code,
	cert,
	callback,
}: {
	act_id: number
	isPartner: boolean
	code?: string
	cert?: CertificateModel
	callback?: Callback
}) {
	const protocolGenerationValidation = yield* select(ActsSelector.getProtocolGenerationValidation)
	let act
	if (protocolGenerationValidation === 0) {
		if (isPartner) {
			act = yield* call(ActsApi.partnerSignAct, [act_id], code)
		} else {
			const file = yield* call(fileApi.getFile, urls.acts.file.replace(':act_id', String(act_id)))
			const dataUrl = yield* call(readFile, file)
			const dataBase64 = getBase64File(dataUrl)
			const signature = yield* call(createSignature, cert.thumbprint, dataBase64, false)
			act = yield* call(ActsApi.DSSignAct, act_id, signature, cert)
		}
	} else {
		act = Deserialize(yield* call(ActsApi.getAct, act_id), ActModel)
	}
	const isNoValide = isPartner ? validateAct(act, true) : validateAct(act, false, cert)
	if (isNoValide) {
		if (protocolGenerationValidation < 1) {
			yield* put(actSlice.actions.setUserSertificates([]))
			yield* put(
				actSlice.actions.counterProtocolGenerationValidation(protocolGenerationValidation + 1)
			)
			if (isPartner) {
				return yield* call(onValidate, { act_id, code, isPartner: true })
			}
			yield* call(signAct, Deserialize(act, ActModel), cert, act_id)
		} else {
			yield* put(actSlice.actions.setUserSertificates([]))
			yield* put(
				actSlice.actions.counterProtocolGenerationValidation(protocolGenerationValidation + 1)
			)
			if (isPartner) {
				yield* call(partnerSignAct, Deserialize(act, ActModel), act_id)
			} else {
				yield* call(signAct, Deserialize(act, ActModel), cert, act_id)
			}
			callback?.()
		}
	} else {
		if (isPartner) {
			yield* call(partnerSignAct, Deserialize(act, ActModel), act_id)
		} else {
			yield* call(signAct, Deserialize(act, ActModel), cert, act_id)
		}
		callback?.()
	}
}

function* signAct(act: ActModel, cert: CertificateModel, act_id: number) {
	try {
		const protocol = yield* call(
			makeProtocolSaga,
			Deserialize(
				{
					...act,
					ds_signing_at: new Date().toISOString().split('T')[0],
					certificate_info: { subjectName: cert.subjectName, thumbprint: cert.thumbprint },
				},
				ActModel
			)
		)
		const isNoValide = validateAct(act, false, cert)
		if (isNoValide) {
			yield* call(ActsApi.sendActProtocol, protocol, act_id, isNoValide)
		} else {
			yield* call(ActsApi.sendActProtocol, protocol, act_id)
		}
		yield* put(actSlice.actions.counterProtocolGenerationValidation(0))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* DSSignActsSaga(action: actNS.DSSignActs) {
	try {
		yield* call(notifyMessageCreator, 'acts.sign.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { certNumber, ids, callback } = action.payload
		const certState = yield* select(ActsSelector.getUserCertificates)
		const cert = certState.find((c: CertificateModel): boolean => c.thumbprint === certNumber)
		yield* all(ids.map((act_id) => call(onValidate, { act_id, cert, isPartner: false, callback })))
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.sign.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getUserSertificatesSaga() {
	try {
		const certState = yield* select(ActsSelector.getUserCertificates)
		if (certState && certState.length) return
		yield* call(notifyMessageCreator, 'acts.search_certificates.in_progress')
		yield* delay(3000)
		yield* put(loadersSlice.actions.showGlobalLoader())
		let certificates = yield* call(getUserCertificates)
		certificates = (certificates || []).map((cert) => Deserialize(cert, CertificateModel))
		yield* put(actSlice.actions.setUserSertificates(certificates))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getActsCancelKindsSaga(action: actNS.getActsCancelKinds) {
	try {
		const result = yield* call(ActsApi.getCancelKindsActs, action.payload)
		const actsCancelKinds = new ResponseCatalog<KindActModel>()
		actsCancelKinds.entries = result.map((i: unknown): KindActModel => Deserialize(i, KindActModel))
		yield* put(actSlice.actions.setActsCancelKinds(actsCancelKinds))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* cancelActsSaga(action: actNS.cancelActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, kind, message, callback } = action.payload
		yield* call(ActsApi.cancelActs, act_ids, kind, message)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.cancel.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* deleteActsSaga(action: actNS.deleteActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { ids, callback } = action.payload
		yield* call(ActsApi.deleteActs, ids)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.delete.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* restoreActsSaga(action: actNS.restoreActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { ids, callback } = action.payload
		yield* call(ActsApi.restoreActs, ids)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.restore.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* massDeleteActsSaga(action: actNS.deleteMassActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { created_at_from, created_at_to, callback } = action.payload
		yield* call(ActsApi.massDeleteActs, created_at_from, created_at_to)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const data = [getDate(created_at_from), getDate(created_at_to)]
		yield* call(notifyMessageCreator, 'acts.mass_delete.success', { data })
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* onPartnerSignMassActsSaga(action: actNS.onPartnerSignMassActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { created_at_from, created_at_to, callback } = action.payload
		yield* call(ActsApi.onPartnerSignMassActs, created_at_from, created_at_to)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const data = [getDate(created_at_from), getDate(created_at_to)]
		yield* call(notifyMessageCreator, 'acts.mass_sign.success', { data })
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* clearUnusedActsSaga(action: actNS.clearUnusedSms) {
	try {
		yield* call(ActsApi.clearUnusedSms, action.payload)
		yield* call(notifyMessageCreator, 'common.sms_clear.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* unBlockActSaga(action: actNS.unblockAct) {
	try {
		yield* call(ActsApi.unblockAct, action.payload)
		yield* call(getAllActs, true)
		yield* call(notifyMessageCreator, 'acts.unblock.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* generateProtocol(actId: number) {
	const res = yield* call(ActsApi.getAct, actId)
	const act = Deserialize(res, ActModel)
	const isDS =
		act?.status?.status_slug === ActStatuses.ANNUL ||
		act?.status?.status_slug === ActStatuses.ON_ANNUL_DS
	if (GenerateProtocolStatuses.has(act?.status?.status_slug) || isDS) {
		yield* call(sendProtocolSaga, act)
	}
}

function* generateProtocolsSaga(action: actNS.generateProtocol) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, callback } = action.payload
		yield* all(act_ids.map((actId) => call(generateProtocol, actId)))
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.generate_protocols.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* refundActsSaga(action: actNS.refundActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, callback } = action.payload
		yield* call(ActsApi.refundActs, act_ids)
		yield* put(actSlice.actions.clearActs())
		yield* call(getAllActs)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.refund.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* archiveActsSaga(action: actNS.archiveActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.archiveActs, action.payload)
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* getActsFileSaga(action: actNS.getActsFile) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { file, filename } = action.payload
		const res = yield* call(fileApi.getFile, file)
		const url = window.URL.createObjectURL(res)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		downloadFile(url, filename)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* partnerCanSignSaga(action: actNS.partnerCanSign) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, callback } = action.payload
		yield* call(ActsApi.partnerCanSign, act_ids)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.change.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* sendRefundEmailsSaga(action: actNS.sendRefundEmails) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, callback } = action.payload
		yield* call(ActsApi.sendRefundEmails, act_ids)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.refund_email.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* uploadPaymentOrdersSaga(action: actNS.uploadPaymentOrders) {
	try {
		yield* call(notifyMessageCreator, 'acts.upload_payment_orders.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadPaymentOrders, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.upload_payment_orders.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* uploadThirdPartyActsAndInvoicesSaga(action: actNS.uploadThirdPartyActsAndInvoices) {
	try {
		yield* call(notifyMessageCreator, 'acts.upload_third_party_acts_and_invoices.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadThirdPartyActsAndInvoices, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.upload_third_party_acts_and_invoices.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* generateProtocolOnOpenSaga(action: actNS.generateProtocolOnOpen) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(generateProtocol, action.payload)
		const res = yield* call(ActsApi.getAct, action.payload)
		const actWithProtocol = Deserialize(res, ActModel)
		const openedAct = yield* select(ActsSelector.getOpenedAct)
		if (openedAct === action.payload) {
			yield* put(actSlice.actions.setAct(actWithProtocol))
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.generate_protocol.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* remarkActsSaga(action: actNS.remarkActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, message, callback } = action.payload
		yield* call(ActsApi.remarkActs, act_ids, message)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'acts.remark.success')
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		const codeError = get(Object.keys(get(error, 'data.errors[0]', {})), '[0]', '')
		yield* put(
			showNotificationAction({
				type: NOTIFICATION_TYPE.ERROR,
				message: getCodeTranslate(`errors.${codeError}`),
			})
		)
	}
}
function* uploadReestrSaga(action: actNS.uploadReestr) {
	try {
		yield* call(notifyMessageCreator, 'reestr.upload.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadReestr, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'reestr.upload.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* uploadPromotionReestrSaga(action: actNS.uploadPromotionReestr) {
	try {
		yield* call(notifyMessageCreator, 'reestr.upload.start')
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(ActsApi.uploadPromotionReestr, action.payload)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'reestr.upload.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* annulActsSaga(action: actNS.annulActs) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { act_ids, callback } = action.payload
		yield* call(ActsApi.annulActs, act_ids)
		yield* call(getAllActs, true)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		callback?.()
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export const actSaga = function* saga() {
	yield* takeLeading(actSlice.actions.getActs.type, getActsSaga)
	yield* takeLeading(actSlice.actions.getSignableActs.type, getSignableActsSaga)
	yield* takeLeading(actSlice.actions.getAct.type, getActSaga)
	yield* takeLeading(actSlice.actions.uploadActs.type, uploadActsSaga)
	yield* takeLeading(actSlice.actions.uploadActsStatuses.type, uploadActsStatusesSaga)
	yield* takeLeading(actSlice.actions.createActSmsCode.type, createActsSMSCodeSaga)
	yield* takeLeading(actSlice.actions.partnerSignActs.type, partnerSignActsSaga)
	yield* takeLeading(actSlice.actions.getUserSerticates.type, getUserSertificatesSaga)
	yield* takeLeading(actSlice.actions.DSSignActs.type, DSSignActsSaga)
	yield* takeLeading(actSlice.actions.getActsCancelKinds.type, getActsCancelKindsSaga)
	yield* takeLeading(actSlice.actions.cancelActs.type, cancelActsSaga)
	yield* takeLeading(actSlice.actions.deleteActs.type, deleteActsSaga)
	yield* takeLeading(actSlice.actions.restoreActs.type, restoreActsSaga)
	yield* takeLeading(actSlice.actions.clearUnusedSms.type, clearUnusedActsSaga)
	yield* takeLeading(actSlice.actions.unblockAct.type, unBlockActSaga)
	yield* takeLeading(actSlice.actions.cancelPaids.type, cancelActsPaidSaga)
	yield* takeLeading(actSlice.actions.deleteMassActs.type, massDeleteActsSaga)
	yield* takeLeading(actSlice.actions.onPartnerSignMassActs.type, onPartnerSignMassActsSaga)
	yield* takeLeading(actSlice.actions.generateProtocol.type, generateProtocolsSaga)
	yield* takeLeading(actSlice.actions.generateProtocolOnOpen.type, generateProtocolOnOpenSaga)
	yield* takeLeading(actSlice.actions.refundActs.type, refundActsSaga)
	yield* takeLeading(actSlice.actions.archiveActs.type, archiveActsSaga)
	yield* takeLeading(actSlice.actions.getActsFile.type, getActsFileSaga)
	yield* takeLeading(actSlice.actions.partnerCanSign.type, partnerCanSignSaga)
	yield* takeLeading(actSlice.actions.sendRefundEmails.type, sendRefundEmailsSaga)
	yield* takeLeading(actSlice.actions.uploadPaymentOrders.type, uploadPaymentOrdersSaga)
	yield* takeLeading(
		actSlice.actions.uploadThirdPartyActsAndInvoices.type,
		uploadThirdPartyActsAndInvoicesSaga
	)
	yield* takeLeading(actSlice.actions.remarkActs.type, remarkActsSaga)
	yield* takeLeading(actSlice.actions.uploadReestr.type, uploadReestrSaga)
	yield* takeLeading(actSlice.actions.annulActs.type, annulActsSaga)
	yield* takeLeading(actSlice.actions.uploadPromotionReestr.type, uploadPromotionReestrSaga)
}
